import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Eye, EyeOff } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { z } from "zod";

import { useResetPasswordMutation } from "../api/useResetPasswordMutation";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/v2/alert";
import { Button } from "@/components/ui/v2/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/v2/form";
import { Input } from "@/components/ui/v2/input";
import { ROUTES } from "@/constants/routes";
import { ErrorResponse } from "@/types/response/ErrorResponse";
import { ValidationResponse } from "@/types/response/ValidationResponse";

interface SetPasswordFormProps {
  email: string;
  token: string;
}

const setPasswordSchema = z
  .object({
    password: z.string().min(1, "Password is required"),
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type SetPasswordFormData = z.infer<typeof setPasswordSchema>;

export function SetPasswordForm({ email, token }: SetPasswordFormProps) {
  const { t } = useTranslation();
  const resetPasswordMutation = useResetPasswordMutation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const form = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const onSubmit = (data: SetPasswordFormData) => {
    resetPasswordMutation.mutate({
      email,
      token,
      password: data.password,
      confirmPassword: data.confirmPassword,
    });
  };

  // Get field errors from mutation error
  const getFieldError = (field: string) => {
    const response = resetPasswordMutation.failureReason?.response;
    if (!response) {
      return undefined;
    }
    const error = response.data;
    if (!error || !Array.isArray(error)) {
      return undefined;
    }
    const validationError = (error as ValidationResponse).find(
      (e) => e.propertyName === field,
    );
    if (!validationError) return undefined;
    return t(`errorCodes.${validationError.errorMessage}`);
  };

  // Get auth error (non-validation errors)
  const getAuthError = () => {
    const response = resetPasswordMutation.failureReason?.response;
    if (!response) {
      return undefined;
    }
    const error = response.data;
    if (!error || !("code" in error)) {
      return undefined;
    }
    return t(`errorCodes.${(error as ErrorResponse).code}`);
  };

  const authError = getAuthError();
  const passwordError = getFieldError("password");
  const confirmPasswordError = getFieldError("confirmPassword");

  // Sync server errors to form
  useEffect(() => {
    if (passwordError) {
      form.setError("password", { message: passwordError });
    }
    if (confirmPasswordError) {
      form.setError("confirmPassword", { message: confirmPasswordError });
    }
  }, [passwordError, confirmPasswordError, form]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        {authError && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{authError}</AlertTitle>
            <AlertDescription>
              {t("auth.pages.changePassword.invalidToken")}
              <Button
                asChild
                variant="outline"
                size="sm"
                className="mt-2 text-foreground"
              >
                <Link to={ROUTES.forgotPassword}>
                  {t("auth.pages.login.forgotPassword")}
                </Link>
              </Button>
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-4">
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.pages.changePassword.newPassword")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      placeholder={t("auth.pages.changePassword.newPassword")}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        resetPasswordMutation.reset();
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowPassword(!showPassword);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="confirmPassword"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("auth.pages.changePassword.confirmNewPassword")}</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder={t("auth.pages.changePassword.newPassword")}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        resetPasswordMutation.reset();
                      }}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setShowConfirmPassword(!showConfirmPassword);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                      tabIndex={-1}
                    >
                      {showConfirmPassword ? (
                        <EyeOff className="h-4 w-4" />
                      ) : (
                        <Eye className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <Button
          type="submit"
          disabled={resetPasswordMutation.isPending}
          className="w-full mt-4"
        >
          {resetPasswordMutation.isPending
            ? t("common.loading", "Loading...")
            : t("auth.pages.changePassword.savePassword")}
        </Button>
      </form>
    </Form>
  );
}
